import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import "../assets/css/social.css";
import urls from "../assets/data/url.json";
import socials from "../assets/data/social.json";
import frFlag from "../assets/img/countries/fr.png";
import usFlag from "../assets/img/countries/us.png";

const Social = () => {
    const { t, i18n } = useTranslation();
    const [isLanguageMenuOpen, setIsLanguageMenuOpen] = useState(false);
    const languageMenuRef = useRef(null);

    const changeLanguage = (language) => {
        i18n.changeLanguage(language);
        setIsLanguageMenuOpen(false);
    };

    const languageOptions = useMemo(
        () => [
            {
                value: "fr",
                label: t("common.language.fr"),
                flag: frFlag,
            },
            {
                value: "en",
                label: t("common.language.en"),
                flag: usFlag,
            },
        ],
        [t],
    );

    const normalizedLanguage = i18n.language.startsWith("fr") ? "fr" : "en";
    const activeLanguage =
        languageOptions.find((option) => option.value === normalizedLanguage) ||
        languageOptions[0];

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (
                languageMenuRef.current &&
                !languageMenuRef.current.contains(event.target)
            ) {
                setIsLanguageMenuOpen(false);
            }
        };

        const handleEscape = (event) => {
            if (event.key === "Escape") {
                setIsLanguageMenuOpen(false);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        document.addEventListener("keydown", handleEscape);

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
            document.removeEventListener("keydown", handleEscape);
        };
    }, []);

    return (
        <>
            <div className="social" id="social">
                <div className="social-status">
                    <span className="dot"></span>
                    {t("social.status")}
                </div>

                <div className="social-sec">
                    {/* URL */}

                    <div className="social-icon">
                        {Object.entries(urls).map(
                            ([key, { url, icon, label }]) => (
                                <a
                                    key={key}
                                    href={url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    aria-label={label || key}
                                    title={label || key}
                                >
                                    <i className={icon} aria-hidden="true"></i>
                                </a>
                            ),
                        )}
                    </div>

                    {/* Contact */}

                    <div className="social-contact">
                        {Object.entries(socials).map(
                            ([key, { value, icon, link }]) => (
                                <div className="social-contact-item" key={key}>
                                    <a
                                        href={link}
                                        aria-label={
                                            key === "phone" ?
                                                `Appeler le ${value}`
                                            : key === "email" ?
                                                `Envoyer un e-mail à ${value}`
                                            :   `${key} : ${value}`
                                        }
                                    >
                                        <i
                                            className={icon}
                                            aria-hidden="true"
                                        ></i>
                                        <span>{value}</span>
                                    </a>

                                    {key === "email" && (
                                        <div
                                            className="social-language"
                                            ref={languageMenuRef}
                                            role="group"
                                            aria-label={t(
                                                "common.language.label",
                                            )}
                                        >
                                            <button
                                                type="button"
                                                className={`social-language-trigger ${isLanguageMenuOpen ? "is-open" : ""}`}
                                                onClick={() =>
                                                    setIsLanguageMenuOpen(
                                                        (current) => !current,
                                                    )
                                                }
                                                aria-label={t(
                                                    "common.language.label",
                                                )}
                                                aria-haspopup="listbox"
                                                aria-expanded={
                                                    isLanguageMenuOpen
                                                }
                                            >
                                                <img
                                                    src={activeLanguage.flag}
                                                    alt=""
                                                    className="social-language-flag"
                                                    draggable="false"
                                                />
                                                <span className="social-language-label">
                                                    {activeLanguage.label}
                                                </span>
                                                <i
                                                    className="fa-solid fa-chevron-down social-language-chevron"
                                                    aria-hidden="true"
                                                ></i>
                                            </button>

                                            {isLanguageMenuOpen && (
                                                <ul
                                                    className="social-language-menu"
                                                    role="listbox"
                                                    aria-label={t(
                                                        "common.language.label",
                                                    )}
                                                >
                                                    {languageOptions.map(
                                                        (option) => (
                                                            <li
                                                                key={
                                                                    option.value
                                                                }
                                                                role="option"
                                                                aria-selected={
                                                                    option.value ===
                                                                    normalizedLanguage
                                                                }
                                                            >
                                                                <button
                                                                    type="button"
                                                                    className={`social-language-option ${option.value === normalizedLanguage ? "is-active" : ""}`}
                                                                    onClick={() =>
                                                                        changeLanguage(
                                                                            option.value,
                                                                        )
                                                                    }
                                                                >
                                                                    <img
                                                                        src={
                                                                            option.flag
                                                                        }
                                                                        alt=""
                                                                        className="social-language-flag"
                                                                        draggable="false"
                                                                    />
                                                                    <span>
                                                                        {
                                                                            option.label
                                                                        }
                                                                    </span>
                                                                </button>
                                                            </li>
                                                        ),
                                                    )}
                                                </ul>
                                            )}
                                        </div>
                                    )}
                                </div>
                            ),
                        )}
                    </div>
                </div>
            </div>
        </>
    );
};

export default Social;
